import type { Figure } from "@howcomet/shared";
import type { FigureProps } from "./registry";

const COLORS = ["var(--color-cyan)", "var(--color-pink)", "var(--color-mint)"];

/**
 * Waves side by side, travelling at the same speed.
 *
 * Same speed is the whole point and the easiest thing to get wrong: if the
 * tighter wave also moved faster the picture would say something false. They
 * all take the same time to cross the same distance, so the only difference a
 * child sees is how many waves fit — which is what pitch and colour actually
 * are.
 */
export function WaveFigure({ figure }: FigureProps<Figure>) {
  const labels = figure.labels ?? [];
  const values = figure.values ?? [];
  if (labels.length < 2 || values.length !== labels.length) return null;

  const width = 300;
  const rowHeight = 62;
  const amplitude = 17;

  return (
    <div
      role="img"
      aria-label={labels
        .map((l, i) => `${l}: ${values[i]} waves in the same space`)
        .join(". ")}
    >
      <svg
        viewBox={`0 0 ${width} ${labels.length * rowHeight}`}
        className="mx-auto block h-auto w-full max-w-[340px]"
      >
        {labels.map((label, row) => {
          // Round, because half a wave at the edge shows the seam when the
          // path slides and gives away that it is one drawing moving.
          const cycles = Math.max(1, Math.round(values[row] ?? 1));
          const period = width / cycles;
          const midY = row * rowHeight + rowHeight / 2 + 6;

          // Two extra periods of run-up, so sliding one period leaves the
          // frame full at every moment of the loop.
          let d = `M ${-period * 2} ${midY}`;
          for (let i = -2; i < cycles + 2; i += 1) {
            const x = i * period;
            d += ` q ${period / 4} ${-amplitude}, ${period / 2} 0 q ${period / 4} ${amplitude}, ${period / 2} 0`;
          }

          return (
            <g key={label}>
              <text
                x="0"
                y={row * rowHeight + 14}
                fontSize="12"
                fontWeight="700"
                fill="var(--color-cream)"
                opacity="0.8"
              >
                {label}
              </text>
              <g clipPath="url(#wave-clip)">
                <path
                  d={d}
                  fill="none"
                  stroke={COLORS[row % COLORS.length]}
                  strokeWidth="3"
                  strokeLinecap="round"
                  className="wave-travel"
                  style={{ ["--wave-period" as string]: `${period}px` }}
                />
              </g>
            </g>
          );
        })}
        <defs>
          <clipPath id="wave-clip">
            <rect x="0" y="0" width={width} height={labels.length * rowHeight} />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}
